import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { blur, touch, Field, reset, change as changeForm } from 'redux-form';
import classNames from 'classnames';
import _ from 'lodash';
import MediaQuery from 'react-responsive';
import { useTranslation } from 'react-i18next';
import ActionButton from 'jbc-front/components/ActionButton';
import { Edit, Strage, MinusCircle } from 'jbc-front/components/icons';
import { concatParamsToUrl } from 'src/utils/Http';
import { EMPLOYEE_SHOW } from 'src/constants/EndpointUrls';

import { ModifyIcon } from 'src/icons';
import { toHourFormat, toFloatFormat, toDisplayFormat, toDbFormat } from 'src/utils/Completion';
import { convertTimeToMinutes, formatMinutesToTime } from 'src/utils/Number';
import { isNumberFormat, isHourFormat } from 'src/utils/CustomValidators';
import { ErrorsSection } from 'src/components';
import {
  REDUCER_NAME,
  setActionMode,
  getImportJobs,
  getClientSyncTasks,
  getCustomApiSyncJobs,
  getCustomApiSemiMonthlyAttendanceSyncJobs
} from 'src/reducers/employees/employeeAttendances';
import { EMPLOYEE_ATTENDANCE_FORM } from 'src/constants/FormNames';
import { getItemExtra } from 'src/reducers/common/itemReducer';
import * as CssClassNames from 'src/constants/CssClassNames';
import MemoList from 'src/features/Memos';
import AllAttendances from './AllAttendances';
import styles from './EmployeeAttendanceForm.scss';

const PREDETERMINED_WORKING_DAYS = 'predetermined_working_days';
const PREDETERMINED_WORKING_TIME = 'predetermined_working_time';

const allowanceItemValidate = unitType => {
  switch (unitType) {
    case 'time':
      return [isHourFormat];
    default:
      return [isNumberFormat];
  }
};

const allowanceItemFormat = (unitType, value) => {
  switch (unitType) {
    case 'time':
      return toHourFormat(value);
    default:
      return toFloatFormat(value);
  }
};

const renderValue = ({ input, disabled, meta: { touched, error }, modified }) => {
  const { t } = useTranslation();
  const [displayValue, setDisplayValue] = useState(() => toDisplayFormat(input.value));

  useEffect(
    () => {
      setDisplayValue(prev => {
        const normalizedPrev = toDbFormat(prev);
        return input.value !== normalizedPrev ? toDisplayFormat(input.value) : prev;
      });
    },
    [input.value]
  );

  const handleChange = e => {
    const inputValue = e.target.value;
    setDisplayValue(inputValue);
    input.onChange(toDbFormat(inputValue));
  };

  return (
    <div {...(touched && error ? { className: `invalid ${styles.def}` } : { className: styles.def })}>
      <div className="m-table-obvious-modified">
        <ModifyIcon modified={modified} size={14} />
      </div>
      <input {...input} value={displayValue} onChange={handleChange} disabled={disabled} />
      {touched && error && <p className={styles.error}>{t('features.EmployeeAttendances.components.EmployeeAttendanceForm.the_format_is_incorrect')}</p>}
    </div>
  );
};

const RowBlank = () => (
  <tr>
    <th>&nbsp;</th>
    <td className="m-table-obvious-blank">&nbsp;</td>
  </tr>
);

const Row = props => {
  const { label, name, validate, onBlur, onChange, disabled, modified } = props;
  return (
    <tr>
      <th>{label}</th>
      <td>
        <Field
          component={renderValue}
          type="text"
          name={name}
          validate={validate}
          onBlur={onBlur}
          onChange={onChange}
          disabled={disabled}
          modified={modified}
        />
      </td>
    </tr>
  );
};

const EmployeeAttendanceForm = props => {
  const {
    dispatch,
    handleSubmit,
    submit,
    errors,
    pristine,
    submitting,
    employeeId,
    employeeName,
    allowanceItems,
    actionMode,
    setMode,
    resetForm,
    attendanceStartDate,
    attendanceEndDate,
    paymentDate,
    isEmployeePayConfirmed,
    allAttendances,
    employeeAttendanceId,
    attendanceSyncJobs,
    attendanceImportJobs,
    customApiSyncJobs,
    customApiSemiMonthlyAttendanceSyncJobs,
    prescribedWorkingHoursPerDay
  } = props;
  const employeeDetailUrl = concatParamsToUrl(EMPLOYEE_SHOW, { id: employeeId });

  const prescribedWorkingTimeItem = allowanceItems.time.find(item => item.itemCode === PREDETERMINED_WORKING_TIME);

  const handlePredeterminedWorkingDaysChange = (_e, newValue) => {
    if (!prescribedWorkingTimeItem || !prescribedWorkingHoursPerDay) return;

    const days = parseFloat(newValue);
    if (Number.isNaN(days) || days < 0) return;

    const totalMinutes = days * convertTimeToMinutes(prescribedWorkingHoursPerDay);
    dispatch(changeForm(EMPLOYEE_ATTENDANCE_FORM, prescribedWorkingTimeItem.name, formatMinutesToTime(totalMinutes)));
  };

  const numberOfDaysLength = allowanceItems.numberOfDays.length;
  const timeLength = allowanceItems.time.length;
  const numberOfTimesLength = allowanceItems.numberOfTimes.length;
  const maxLength = _.max([numberOfDaysLength, timeLength, numberOfTimesLength]);
  const isViewMode = actionMode === 'view';

  const hasRunningJobs =
    !_.isEmpty(attendanceSyncJobs.inProgress) ||
    !_.isEmpty(attendanceSyncJobs.waiting) ||
    !_.isEmpty(attendanceImportJobs.inProgress) ||
    !_.isEmpty(attendanceImportJobs.waiting) ||
    !_.isEmpty(customApiSyncJobs.inProgress) ||
    !_.isEmpty(customApiSyncJobs.waiting) ||
    !_.isEmpty(customApiSemiMonthlyAttendanceSyncJobs.inProgress) ||
    !_.isEmpty(customApiSemiMonthlyAttendanceSyncJobs.waiting);
  const disabledEditBtn = isEmployeePayConfirmed || hasRunningJobs;
  const { t } = useTranslation();
  const cantChangeAllowance = t('features.EmployeeAttendances.components.EmployeeAttendanceForm.cant_change_allowance_items_now');
  return (
    <form className={classNames(CssClassNames.FORM, CssClassNames.CLIENT_FORM)} onSubmit={handleSubmit}>
      <ErrorsSection errors={errors} />
      <div>
        <div className={styles.name}>
          <div className={styles.memo}>
            <MemoList employeeId={employeeId} size={44} />
          </div>
          <div className={styles.nameFull}>
            <a href={employeeDetailUrl}>{employeeName}</a>
          </div>
        </div>
        <AllAttendances {...{ attendanceStartDate, attendanceEndDate, paymentDate, employeeAttendanceId, allAttendances }} />
      </div>

      <div className={styles.buttons}>
        <div className={styles.buttonsLeft}>
          {isViewMode && (
            <ActionButton
              onClick={() => setMode('edit')}
              icon={<Edit size={15} />}
              disabled={disabledEditBtn}
              disabledReason={hasRunningJobs ? cantChangeAllowance : null}
            >
              {t('features.EmployeeAttendances.components.EmployeeAttendanceForm.edit')}
            </ActionButton>
          )}
          {!isViewMode && (
            <React.Fragment>
              <ActionButton
                onClick={submit}
                icon={<Strage size={15} />}
                disabled={pristine || submitting || disabledEditBtn}
                disabledReason={hasRunningJobs ? cantChangeAllowance : null}
              >
                {t('features.EmployeeAttendances.components.EmployeeAttendanceForm.save')}
              </ActionButton>
              <ActionButton
                onClick={() => {
                  resetForm();
                  setMode('view');
                }}
                disabled={submitting}
                icon={<MinusCircle size={15} />}
              >
                {t('features.EmployeeAttendances.components.EmployeeAttendanceForm.cancel')}
              </ActionButton>
            </React.Fragment>
          )}
        </div>
      </div>

      <div className={styles.payment}>
        <div className={styles.paymentClm}>
          <table className="m-table-obvious">
            <thead>
              <tr>
                <th colSpan="2">{t('features.EmployeeAttendances.components.EmployeeAttendanceForm.Work_days')}</th>
              </tr>
            </thead>
            <tbody>
              {allowanceItems.numberOfDays.map(item => (
                <Row
                  key={item.name}
                  label={item.label}
                  name={item.name.toString()}
                  validate={allowanceItemValidate(item.unitType)}
                  onBlur={(e, newValue, previousValue) => {
                    e.preventDefault();
                    dispatch(blur(EMPLOYEE_ATTENDANCE_FORM, item.name, allowanceItemFormat(item.unitType, previousValue)));
                    dispatch(touch(EMPLOYEE_ATTENDANCE_FORM, item.name));
                  }}
                  onChange={item.itemCode === PREDETERMINED_WORKING_DAYS ? handlePredeterminedWorkingDaysChange : undefined}
                  disabled={isViewMode}
                />
              ))}
              <MediaQuery minWidth={737}>{_.times(maxLength - numberOfDaysLength, key => <RowBlank key={key} />)}</MediaQuery>
            </tbody>
          </table>
        </div>

        <div className={styles.paymentClm}>
          <table className="m-table-obvious">
            <thead>
              <tr>
                <th colSpan="2">{t('features.EmployeeAttendances.components.EmployeeAttendanceForm.Work_hours')}</th>
              </tr>
            </thead>
            <tbody>
              {allowanceItems.time.map(item => (
                <Row
                  key={item.name}
                  label={item.label}
                  name={item.name.toString()}
                  validate={allowanceItemValidate(item.unitType)}
                  onBlur={(e, newValue, previousValue) => {
                    e.preventDefault();
                    dispatch(blur(EMPLOYEE_ATTENDANCE_FORM, item.name, allowanceItemFormat(item.unitType, previousValue)));
                    dispatch(touch(EMPLOYEE_ATTENDANCE_FORM, item.name));
                  }}
                  disabled={isViewMode}
                />
              ))}
              <MediaQuery minWidth={737}>{_.times(maxLength - timeLength, key => <RowBlank key={key} />)}</MediaQuery>
            </tbody>
          </table>
        </div>

        <div className={styles.paymentClm}>
          <table className="m-table-obvious">
            <thead>
              <tr>
                <th colSpan="2">{t('features.EmployeeAttendances.components.EmployeeAttendanceForm.times')}</th>
              </tr>
            </thead>
            <tbody>
              {allowanceItems.numberOfTimes.map(item => (
                <Row
                  key={item.name}
                  label={item.label}
                  name={item.name.toString()}
                  validate={allowanceItemValidate(item.unitType)}
                  onBlur={(e, newValue, previousValue) => {
                    e.preventDefault();
                    dispatch(blur(EMPLOYEE_ATTENDANCE_FORM, item.name, allowanceItemFormat(item.unitType, previousValue)));
                    dispatch(touch(EMPLOYEE_ATTENDANCE_FORM, item.name));
                  }}
                  disabled={isViewMode}
                />
              ))}
              <MediaQuery minWidth={737}>{_.times(maxLength - numberOfTimesLength, key => <RowBlank key={key} />)}</MediaQuery>
            </tbody>
          </table>
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = state => ({
  actionMode: getItemExtra(REDUCER_NAME, state).actionMode || 'view',
  attendanceImportJobs: getImportJobs(state) || {},
  attendanceSyncJobs: getClientSyncTasks(state) || {},
  customApiSyncJobs: getCustomApiSyncJobs(state) || {},
  customApiSemiMonthlyAttendanceSyncJobs: getCustomApiSemiMonthlyAttendanceSyncJobs(state) || {}
});

const mapDispatchToProps = dispatch => ({
  setMode: mode => dispatch(setActionMode(mode)),
  resetForm: () => dispatch(reset(EMPLOYEE_ATTENDANCE_FORM))
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeAttendanceForm);
